import { BreakpointObserver } from '@angular/cdk/layout';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, TemplateRef, ViewChild } from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { InputObservable, connectState } from '@y42/angular/util';
import { LogModel, LogsHistoryService } from '@y42/log';
import { LogsAgGridComponent } from '@y42/log/shared';
import { PushNotificationService } from '@y42/push-notification';
import {
  BehaviorSubject,
  Observable,
  Subject,
  catchError,
  combineLatest,
  debounceTime,
  map,
  of,
  race,
  shareReplay,
  startWith,
  switchMap,
  tap,
  timer,
} from 'rxjs';
import { JobLogsSideNavTemplateContext } from '../model';

export const LOGS_TIMEOUT_MS = 10_000;

@UntilDestroy()
@Component({
  selector: 'y42-job-logs',
  templateUrl: './job-logs.component.html',
  styleUrls: ['./job-logs.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JobLogsComponent {
  constructor(
    private readonly breakpointObserver: BreakpointObserver,
    private readonly logsService: LogsHistoryService,
    private readonly pushNotificationService: PushNotificationService,
  ) {}
  @Input()
  @InputObservable()
  eventId?: string;
  eventId$!: Observable<string | undefined>;

  @Input()
  sideNavTemplate?: {
    template: TemplateRef<JobLogsSideNavTemplateContext>;
    context: JobLogsSideNavTemplateContext | null;
  };

  @Output() closeDialog: EventEmitter<void> = new EventEmitter();

  @ViewChild('logsAgGrid', { static: false }) logsAgGrid?: LogsAgGridComponent;

  private readonly resizeObserver$ = this.breakpointObserver
    .observe(['(min-width: 1px) and (max-width: 767px)'])
    .pipe(shareReplay({ bufferSize: 1, refCount: true }));
  private readonly isSmallScreen$ = this.resizeObserver$.pipe(map((result) => result.matches));
  private readonly toggleSidebar$$ = new Subject<boolean>();
  private readonly loadingLogs$$ = new BehaviorSubject(true);

  // how to do logs - https://app.clickup.com/t/2575789/TECH-110?comment=745323299
  readonly state = connectState(this, {
    logs: this.eventId$
      .pipe(
        debounceTime(100),
        switchMap((eventId) =>
          eventId
            ? combineLatest([
                this.logsService.getHistoryFromChannel(`v2.${eventId}`),
                this.pushNotificationService.openChannel<LogModel>(`v2.${eventId}`).pipe(
                  map((evt) => evt.data),
                  catchError((err: unknown) => {
                    console.error(err);
                    return of(null);
                  }),
                  tap((log) => {
                    if (log) {
                      this.logsAgGrid?.applyTransaction(log as LogModel);
                    }
                  }),
                  startWith(null),
                ),
              ])
            : of([]),
        ),
      )
      .pipe(
        map(([logs]) => logs),
        tap(() => this.loadingLogs$$.next(false)),
        startWith([]),
      ),
    loadingLogs: race(this.loadingLogs$$, timer(LOGS_TIMEOUT_MS).pipe(map(() => false))),
    isOpened: this.isSmallScreen$.pipe(
      switchMap((isSmallScreen) => {
        return isSmallScreen ? this.toggleSidebar$$ : of(true);
      }),
    ),
    isSmallScreen: this.isSmallScreen$,
  });

  toggleSideBar(isOpened: boolean): void {
    this.toggleSidebar$$.next(isOpened);
  }
}
